import React, { useState, useEffect, useMemo } from "react";
import ContentBox from "./ContentBox";
import { ClientSelectSuministroElectrico } from "./ClientSelectSuministroElectrico";
import DatePicker from "react-datepicker";
import calendar from "../img/calendar.svg";
import moment from "moment";
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import { getPaymentHistorySuministroElectrico } from "../api";
registerLocale("es", es);

export function ServiciosSuministroElectricoHistorialPagos({
  loading: initialLoading,
  selectedSuministroElectrico,
  services,
  setSelectedSuministroElectrico,
}) {
  // Set default dates to last year
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Memoize the formatted dates
  const formattedDates = useMemo(
    () => ({
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
    }),
    [startDate, endDate]
  );

  // Memoize the fetch function
  const fetchPayments = useMemo(() => {
    return () => {
      if (!selectedSuministroElectrico?.abastible_contract_id) {
        setError("Debe seleccionar un suministro eléctrico");
        return;
      }

      if (!startDate || !endDate) {
        setError("Debe seleccionar un rango de fechas");
        return;
      }

      setLoading(true);
      setError(null);
      setMessage(null);
      setPayments([]);

      try {
        getPaymentHistorySuministroElectrico(
          selectedSuministroElectrico.abastible_contract_id,
          formattedDates.start_date,
          formattedDates.end_date,
          (error, response) => {
            setLoading(false);

            if (error) {
              setError(
                error.message || "Error al obtener el historial de pagos"
              );
              setPayments([]);
              return;
            }

            if (response?.data) {
              setPayments(response.data);
              setError(null);
              setMessage(null);
              return;
            }

            if (response?.message) {
              setMessage(response.message);
              setPayments([]);
              setError(null);
              return;
            }

            setError("Error al obtener el historial de pagos");
            setPayments([]);
          }
        );
      } catch (err) {
        console.error("Error:", err);
        setError("Error al obtener el historial de pagos");
        setLoading(false);
        setPayments([]);
      }
    };
  }, [selectedSuministroElectrico?.abastible_contract_id, formattedDates]);

  // Only fetch on initial load or when client_cod changes
  useEffect(() => {
    if (
      selectedSuministroElectrico?.abastible_contract_id &&
      !initialLoading &&
      services
    ) {
      fetchPayments();
    }
  }, [selectedSuministroElectrico?.abastible_contract_id]);

  // Memoize the table rows to prevent unnecessary re-renders
  const tableRows = useMemo(() => {
    if (!payments || payments.length === 0) {
      return (
        <tr>
          <td colSpan={4} style={{ textAlign: "center" }}>
            {message || "No hay pagos para mostrar"}
          </td>
        </tr>
      );
    }

    return payments.map((payment, i) => (
      <tr key={`payment-${i}`}>
        <td>{payment.reference_number}</td>
        <td>{moment(payment.payment_date).format("DD-MM-YYYY")}</td>
        <td>${parseInt(payment.amount).toLocaleString("de")}</td>
        <td>{payment.collector_name}</td>
      </tr>
    ));
  }, [payments, message]);

  return (
    <ContentBox loading={initialLoading || loading || !services}>
      <div className="title-client-container clearfix">
        <h1>Historial de Pagos</h1>
        <div className="client-number">
          <ClientSelectSuministroElectrico
            selected={selectedSuministroElectrico}
            services={services}
            number_field={"client_cod"}
            setSelected={setSelectedSuministroElectrico}
          />
        </div>
      </div>

      <div className="col-100 left d-flex justify-between margin-bottom-40">
        <div className="filter-container grid-column-3">
          <div className="position-relative">
            <img className="calendar" src={calendar} alt="calendario" />
            <DatePicker
              popperModifiers={{ flip: { enabled: false } }}
              maxDate={endDate}
              dateFormat="dd/MM/yyyy"
              placeholderText="Desde"
              selected={startDate}
              locale="es"
              onChange={(date) => {
                setStartDate(date);
                setError(null);
                setMessage(null);
              }}
            />
          </div>
          <div className="position-relative">
            <img className="calendar" src={calendar} alt="calendario" />
            <DatePicker
              popperModifiers={{ flip: { enabled: false } }}
              minDate={startDate}
              maxDate={new Date()}
              dateFormat="dd/MM/yyyy"
              placeholderText="Hasta"
              selected={endDate}
              locale="es"
              onChange={(date) => {
                setEndDate(date);
                setError(null);
                setMessage(null);
              }}
            />
          </div>
          <button
            className="button orange"
            onClick={fetchPayments}
            disabled={loading}
          >
            {loading ? "Cargando..." : "Filtrar"}
          </button>
        </div>
      </div>

      {error && (
        <div className="message margin-bottom-40">
          <p>{error}</p>
        </div>
      )}

      {message && !error && (
        <div className="message margin-bottom-40">
          <p>{message}</p>
        </div>
      )}

      <div className="table-container">
        <table className="table1">
          <thead>
            <tr>
              <td>N° de Boleta</td>
              <td>Fecha de Pago</td>
              <td>Monto</td>
              <td>Pagado en</td>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    </ContentBox>
  );
}
